/**
 * Pipe material specifications.
 * Step 1: Carbon Steel, ASTM A106 Gr.B seamless, Schedule 40,
 * butt-weld (BW) long-radius fittings per ASME B16.9.
 *
 * This file is the single place new materials / schedules get added
 * as the sketch tool grows.
 */

export type MaterialId = "cs-a106-sch40";

export interface PipeSizeSpec {
  /** Nominal pipe size label, e.g. `2"` */
  nps: string;
  /** Nominal size in inches (numeric, for math) */
  nom: number;
  /** Outside diameter, inches */
  od: number;
  /** Wall thickness, inches */
  wall: number;
  /** Weight, pounds per foot (plain end) */
  lbPerFt: number;
  /** LR 90 elbow center-to-end, inches (1.5 x NPS) */
  elbow90: number;
  /** LR 45 elbow center-to-end, inches (0.625 x NPS) */
  elbow45: number;
  /** Straight tee center-to-end on the run, inches (ASME B16.9 "C") */
  teeRun: number;
  /** Straight tee center-to-end on the branch, inches (ASME B16.9 "M") */
  teeBranch: number;
}

export interface MaterialSpec {
  id: MaterialId;
  label: string;
  shortLabel: string;
  spec: string;
  fittingSpec: string;
  joint: string;
  sizes: PipeSizeSpec[];
}

const mk = (
  nps: string,
  nom: number,
  od: number,
  wall: number,
  lbPerFt: number,
  teeC: number,
): PipeSizeSpec => ({
  nps,
  nom,
  od,
  wall,
  lbPerFt,
  elbow90: +(1.5 * nom).toFixed(3),
  elbow45: +(0.625 * nom).toFixed(3),
  teeRun: teeC,
  teeBranch: teeC,
});


export const CS_SCH40: MaterialSpec = {
  id: "cs-a106-sch40",
  label: 'Carbon Steel — A106 Gr.B, Sch 40',
  shortLabel: "CS Sch 40",
  spec: "ASTM A106 Gr.B SMLS",
  fittingSpec: "ASME B16.9 BW LR",
  joint: "Butt Weld",
  sizes: [
    mk('1/2"', 0.5, 0.84, 0.109, 0.85, 1.0),
    mk('3/4"', 0.75, 1.05, 0.113, 1.13, 1.12),
    mk('1"', 1, 1.315, 0.133, 1.68, 1.5),
    mk('1-1/4"', 1.25, 1.66, 0.14, 2.27, 1.88),
    mk('1-1/2"', 1.5, 1.9, 0.145, 2.72, 2.25),
    mk('2"', 2, 2.375, 0.154, 3.65, 2.5),
    mk('2-1/2"', 2.5, 2.875, 0.203, 5.79, 3.0),
    mk('3"', 3, 3.5, 0.216, 7.58, 3.38),
    mk('4"', 4, 4.5, 0.237, 10.79, 4.12),
    mk('5"', 5, 5.563, 0.258, 14.62, 4.88),
    mk('6"', 6, 6.625, 0.28, 18.97, 5.62),
    mk('8"', 8, 8.625, 0.322, 28.55, 7.0),
    mk('10"', 10, 10.75, 0.365, 40.48, 8.5),
    mk('12"', 12, 12.75, 0.406, 53.53, 10.0),

  ],
};

export const MATERIALS: Record<MaterialId, MaterialSpec> = {
  "cs-a106-sch40": CS_SCH40,
};

export const DEFAULT_MATERIAL: MaterialId = "cs-a106-sch40";
export const DEFAULT_SIZE = '2"';

export function getSizeSpec(material: MaterialId, nps: string): PipeSizeSpec {
  const m = MATERIALS[material];
  return m.sizes.find((s) => s.nps === nps) ?? m.sizes[0];
}

/**
 * Concentric butt-weld reducer end-to-end length (ASME B16.9 "H"),
 * keyed by the LARGER nominal size, inches.
 */
const REDUCER_H: Record<number, number> = {
  0.75: 1.5,
  1: 2,
  1.25: 2,
  1.5: 2.5,
  2: 3,
  2.5: 3.5,
  3: 3.5,
  4: 4,
  5: 5,
  6: 5.5,
  8: 6,
  10: 7,
  12: 8,
};

export interface ReducerInfo {
  large: PipeSizeSpec;
  small: PipeSizeSpec;
  /** overall end-to-end length, inches */
  length: number;
  label: string;
}

/** Concentric reducer between two sizes. Returns null if the sizes match. */
export function getReducer(
  material: MaterialId,
  a: string,
  b: string,
): ReducerInfo | null {
  if (a === b) return null;
  const sa = getSizeSpec(material, a);
  const sb = getSizeSpec(material, b);
  const large = sa.nom >= sb.nom ? sa : sb;
  const small = sa.nom >= sb.nom ? sb : sa;
  const length = REDUCER_H[large.nom] ?? +(large.nom * 1.0).toFixed(2);
  return {
    large,
    small,
    length,
    label: `${large.nps} × ${small.nps} Conc Reducer BW`,
  };
}


/** Format inches as feet-inches, e.g. 27.5 -> 2'-3 1/2" */
export function formatFtIn(totalInches: number): string {
  const neg = totalInches < 0;
  const v = Math.abs(totalInches);
  const ft = Math.floor(v / 12);
  const inches = v - ft * 12;
  const whole = Math.floor(inches + 1e-6);
  const frac = inches - whole;
  const sixteenths = Math.round(frac * 16);
  let fracStr = "";
  let w = whole;
  if (sixteenths === 16) {
    w += 1;
  } else if (sixteenths > 0) {
    let n = sixteenths;
    let d = 16;
    while (n % 2 === 0) {
      n /= 2;
      d /= 2;
    }
    fracStr = ` ${n}/${d}`;
  }
  const parts: string[] = [];
  if (ft > 0) parts.push(`${ft}'`);
  if (w > 0 || fracStr || ft === 0) parts.push(`${w}${fracStr}"`);
  return (neg ? "-" : "") + parts.join("-");
}
